import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  acceptAgentShare,
  getAgentShare,
  listAccessibleTenants,
} from "../features/agents/agentApi";
import { getDashboardToken } from "../utils/dashboardAuthParams";

const WORKLOAD_LOGO = "https://cdn.workload.co/wp-content/uploads/new-logo-only-512-512.png";
const SIGNUP_BASE = "https://automate.workload.co/auth/signup";
const SIGNIN_BASE = "https://automate.workload.co/auth/signin";

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
    background:
      theme.palette.type === "dark"
        ? theme.palette.background.default
        : `linear-gradient(180deg, ${theme.palette.primary[50] || "#F0F7FF"} 0%, ${
            theme.palette.grey[50]
          } 48%, ${theme.palette.background.default} 100%)`,
  },
  dialogPaper: {
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
  },
  accentBar: {
    height: 4,
    backgroundColor: theme.palette.primary.main,
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(0.5),
  },
  logo: {
    width: 40,
    height: 40,
    flexShrink: 0,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  },
  title: {
    fontWeight: 800,
    fontSize: theme.typography.pxToRem(22),
    lineHeight: 1.25,
    color: theme.palette.text.primary,
  },
  subtitle: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(14),
  },
  body: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1.55,
  },
  callout: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.background.default
        : theme.palette.primary[50] || "#F0F7FF",
  },
  calloutTitle: {
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  actions: {
    padding: theme.spacing(1, 3, 2.5),
    gap: theme.spacing(1),
  },
  primaryBtn: {
    fontWeight: 700,
    minWidth: 140,
  },
}));

const AgentShareAcceptPage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { shareToken } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [share, setShare] = useState(null);
  const [error, setError] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const nextUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/agents/share/${encodeURIComponent(shareToken || "")}`;
  }, [shareToken]);

  const signupHref = `${SIGNUP_BASE}?next=${encodeURIComponent(nextUrl)}`;
  const signinHref = `${SIGNIN_BASE}?next=${encodeURIComponent(nextUrl)}`;

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      setLoading(true);
      setError(null);
      const token = getDashboardToken();
      setAuthed(Boolean(token));
      try {
        const meta = await getAgentShare(shareToken);
        if (cancelled) return;
        setShare(meta);
        if (token && meta?.status === "pending") {
          const all = await listAccessibleTenants({ excludeCurrent: false }).catch(() => []);
          if (cancelled) return;
          setTenants(all);
          if (all.length === 1) {
            setSelectedTenant(all[0]);
            setPickerOpen(false);
          } else {
            setSelectedTenant(null);
            setPickerOpen(true);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.error || err?.message || "Share not found.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    boot();
    return () => {
      cancelled = true;
    };
  }, [shareToken]);

  const onAccept = async (tenantOverride = null) => {
    const tenant = tenantOverride || selectedTenant;
    if (!tenant?.id) return;
    setAccepting(true);
    setError(null);
    try {
      try {
        localStorage.setItem("tenant", tenant.id);
      } catch (_) {
        /* ignore */
      }
      const result = await acceptAgentShare(shareToken, tenant.id);
      const newId = result?.agent_id || result?.agent?.agent_id;
      if (newId) {
        history.replace(`/agents/${newId}`);
        return;
      }
      setError("Accept succeeded but no agent id was returned.");
    } catch (err) {
      const data = err?.response?.data;
      if (data?.cloned_agent_id) {
        history.replace(`/agents/${data.cloned_agent_id}`);
        return;
      }
      setError(data?.error || err?.message || "Failed to accept share.");
      setShare((prev) => (prev ? { ...prev, status: data?.status || prev.status } : prev));
      setPickerOpen(true);
    } finally {
      setAccepting(false);
    }
  };

  const status = share?.status || "not_found";
  const agentName = share?.agent_name || "this agent";
  const dialogOpen =
    !loading &&
    (status !== "pending" ||
      !authed ||
      tenants.length <= 1 ||
      pickerOpen ||
      Boolean(error));

  const renderHeader = (title, subtitle) => (
    <>
      <div className={classes.accentBar} />
      <DialogTitle disableTypography style={{ paddingBottom: 8 }}>
        <div className={classes.header}>
          <Avatar src={WORKLOAD_LOGO} alt="Workload" className={classes.logo} />
          <Box minWidth={0}>
            <Typography className={classes.title}>{title}</Typography>
            {subtitle ? <Typography className={classes.subtitle}>{subtitle}</Typography> : null}
          </Box>
        </div>
      </DialogTitle>
    </>
  );

  if (loading) {
    return (
      <Box className={classes.page}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box className={classes.page}>
      <Dialog
        open={dialogOpen}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={accepting}
        PaperProps={{ className: classes.dialogPaper, elevation: 0 }}
        onClose={() => {
          if (accepting) return;
          if (status === "pending" && authed && tenants.length > 1) {
            /* keep open — must pick a workspace */
          }
        }}
      >
        {status === "pending" && !authed
          ? renderHeader(
              `Start using ${agentName}`,
              share?.shared_by_name
                ? `${share.shared_by_name} shared this agent with you`
                : "Someone shared a Workload agent with you"
            )
          : null}

        {status === "pending" && authed
          ? renderHeader(
              "Select a workspace",
              `Add “${agentName}” to one of your workspaces`
            )
          : null}

        {status === "accepted"
          ? renderHeader("Invite already used", "This share link can only be accepted once")
          : null}

        {status === "expired"
          ? renderHeader("Invite expired", "Ask the sender to share the agent again")
          : null}

        {(status === "not_found" || (!share && error)) && status !== "pending"
          ? renderHeader("Invalid share link", error || "This invite could not be found")
          : null}

        <DialogContent style={{ paddingTop: 8 }}>
          {status === "pending" && !authed ? (
            <>
              <Typography className={classes.body}>
                Continue with Workload to add this agent to your own workspace. Credentials and run
                history are not shared — you connect your accounts, preview, then start.
              </Typography>
              <Box className={classes.callout}>
                <Typography className={classes.calloutTitle}>What happens next</Typography>
                <Typography variant="body2" color="textSecondary">
                  Create an account or sign in, pick a workspace if you have more than one, then
                  finish setup on the agent.
                </Typography>
              </Box>
            </>
          ) : null}

          {status === "pending" && authed ? (
            <>
              <Typography className={classes.body} paragraph>
                Credentials and run history from the sender are not included. Search and select the
                workspace that should receive the copy.
              </Typography>
              {tenants.length === 0 ? (
                <Typography variant="body2" color="error">
                  No workspaces found on this account. Finish workspace setup in Automate, then
                  return to this link.
                </Typography>
              ) : tenants.length === 1 ? (
                <Box className={classes.callout}>
                  <Typography className={classes.calloutTitle}>Workspace</Typography>
                  <Typography variant="body2" color="textPrimary">
                    {tenants[0].name}
                  </Typography>
                </Box>
              ) : (
                <Autocomplete
                  options={tenants}
                  value={selectedTenant}
                  onChange={(_e, value) => setSelectedTenant(value)}
                  getOptionLabel={(option) => option?.name || ""}
                  getOptionSelected={(option, value) => option?.id === value?.id}
                  disabled={accepting}
                  autoHighlight
                  openOnFocus
                  ListboxProps={{ style: { maxHeight: 280 } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Workspace"
                      variant="outlined"
                      placeholder="Search workspaces…"
                      helperText={`${tenants.length} workspaces available`}
                    />
                  )}
                />
              )}
            </>
          ) : null}

          {status === "accepted" && share?.cloned_agent_id ? (
            <Typography className={classes.body}>
              You can open the agent that was already added from this invite.
            </Typography>
          ) : null}

          {error && status === "pending" ? (
            <Typography variant="body2" color="error" style={{ marginTop: 12 }}>
              {error}
            </Typography>
          ) : null}
        </DialogContent>

        <DialogActions className={classes.actions}>
          {status === "pending" && !authed ? (
            <>
              <Button
                variant="outlined"
                href={signinHref}
                style={{
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                }}
              >
                Sign in
              </Button>
              <Button
                variant="contained"
                color="primary"
                href={signupHref}
                className={classes.primaryBtn}
              >
                Continue with Workload
              </Button>
            </>
          ) : null}

          {status === "pending" && authed ? (
            <Button
              variant="contained"
              color="primary"
              className={classes.primaryBtn}
              disabled={accepting || (!selectedTenant && tenants.length !== 1)}
              onClick={() => onAccept(tenants.length === 1 ? tenants[0] : null)}
              startIcon={accepting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {accepting ? "Adding agent…" : "Add agent to workspace"}
            </Button>
          ) : null}

          {status === "accepted" && share?.cloned_agent_id ? (
            <Button
              variant="contained"
              color="primary"
              className={classes.primaryBtn}
              onClick={() => history.push(`/agents/${share.cloned_agent_id}`)}
            >
              Open agent
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentShareAcceptPage;
